import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, CheckCircle2 } from 'lucide-react';

interface BatchSite { site_id: string; site_reference: string; site_name: string; included: boolean }
interface Batch { id: string; client_name: string; template_name: string; status: string; due_date?: string; sites: BatchSite[] }

const StatusBadge: React.FC<{ s: string }> = ({ s }) => {
  const map: Record<string, [string, any]> = {
    pending_broker_review: ['Awaiting your review', 'cyan'],
    pending_client_allocation: ['Approved — with client', 'blue'],
    completed: ['Allocated', 'success'],
    cancelled: ['Cancelled', 'muted'],
  };
  const [l, v] = map[s] || [s, 'muted'];
  return <Badge variant={v}>{l}</Badge>;
};

export const BrokerSurveyReview: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [included, setIncluded] = useState<Record<string, Set<string>>>({});
  const [busy, setBusy] = useState('');

  const load = () => api.get<Batch[]>('/survey-batches').then(r => {
    setBatches(r.data);
    const seed: Record<string, Set<string>> = {};
    r.data.forEach(b => { seed[b.id] = new Set((b.sites || []).filter(s => s.included).map(s => s.site_id)); });
    setIncluded(seed);
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const toggle = (bid: string, sid: string) => setIncluded(prev => {
    const cur = new Set(prev[bid] || []); cur.has(sid) ? cur.delete(sid) : cur.add(sid);
    return { ...prev, [bid]: cur };
  });

  const approve = async (b: Batch) => {
    const ids = Array.from(included[b.id] || []);
    if (ids.length === 0) { toast.error('Keep at least one site to approve.'); return; }
    setBusy(b.id);
    try {
      await api.post(`/survey-batches/${b.id}/broker-approve`, { included_site_ids: ids });
      toast.success('Approved and forwarded to the client.');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not approve.');
    } finally { setBusy(''); }
  };

  const pending = batches.filter(b => b.status === 'pending_broker_review');
  const others = batches.filter(b => b.status !== 'pending_broker_review');

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Survey approvals</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Review surveys issued for your clients. Deselect any sites that don&apos;t apply, then approve to send to the client.</p>
      </div>

      {pending.length === 0 && (
        <Card><CardContent className="py-8 text-center text-sm text-raptor-navy/50 font-body" data-testid="broker-review-empty">No surveys awaiting your review.</CardContent></Card>
      )}

      {pending.map(b => (
        <Card key={b.id} data-testid={`broker-batch-${b.id}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{b.client_name}</CardTitle>
                <CardDescription>{b.template_name}{b.due_date ? ` · due ${b.due_date}` : ''}</CardDescription>
              </div>
              <StatusBadge s={b.status} />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="border border-border/70 rounded-md divide-y divide-border/50">
              {b.sites.map(s => {
                const on = (included[b.id] || new Set()).has(s.site_id);
                return (
                  <label key={s.site_id} className="flex items-center gap-3 px-3 py-2 hover:bg-raptor-neutral/40 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggle(b.id, s.site_id)} data-testid={`broker-site-${s.site_id}`} />
                    <MapPin size={14} className="text-raptor-blue" />
                    <span className="text-sm text-raptor-navy">{s.site_name || s.site_reference}</span>
                    {s.site_reference && <span className="text-[11px] text-raptor-navy/50 font-tabular">{s.site_reference}</span>}
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button variant="gradient" disabled={busy === b.id} onClick={() => approve(b)} data-testid={`broker-approve-${b.id}`}>
                <CheckCircle2 size={14} /> {busy === b.id ? 'Approving…' : `Approve ${(included[b.id] || new Set()).size} site(s)`}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {others.length > 0 && (
        <Card>
          <CardHeader><CardTitle>History</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border/50">
            {others.map(b => (
              <div key={b.id} className="py-2 flex items-center justify-between">
                <div className="text-sm text-raptor-navy">{b.client_name} <span className="text-raptor-navy/50">· {b.template_name}</span></div>
                <StatusBadge s={b.status} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
